import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Mail, ShieldCheck } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { requestEmailChange, verifyEmailChange } from "@/services/auth";

interface ChangeEmailDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	currentEmail: string;
	onSuccess: () => void;
}

type Step = "enter-email" | "enter-otp";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ChangeEmailDialog({
	open,
	onOpenChange,
	currentEmail,
	onSuccess,
}: ChangeEmailDialogProps) {
	const { toast } = useToast();
	const [step, setStep] = useState<Step>("enter-email");
	const [newEmail, setNewEmail] = useState("");
	const [otp, setOtp] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!open) {
			setStep("enter-email");
			setNewEmail("");
			setOtp("");
			setIsLoading(false);
		}
	}, [open]);

	const extractError = (err: unknown, fallback: string) =>
		err && typeof err === "object" && "message" in err
			? (err as { message: string }).message
			: fallback;

	const handleSendCode = async (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = newEmail.trim().toLowerCase();
		if (!EMAIL_REGEX.test(trimmed)) {
			toast({
				title: "Invalid email",
				description: "Enter a valid email address.",
				variant: "destructive",
			});
			return;
		}
		if (trimmed === currentEmail.trim().toLowerCase()) {
			toast({
				title: "Invalid email",
				description: "New email must differ from current email.",
				variant: "destructive",
			});
			return;
		}

		setIsLoading(true);
		try {
			await requestEmailChange(trimmed);
			setStep("enter-otp");
			toast({
				title: "Code sent",
				description: `We emailed a 6-digit code to ${trimmed}. It expires in 10 minutes.`,
				variant: "success",
			});
		} catch (err: unknown) {
			toast({
				title: "Error",
				description: extractError(err, "Failed to send verification code."),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = otp.trim();
		if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) {
			toast({
				title: "Invalid code",
				description: "Enter the 6-digit verification code.",
				variant: "destructive",
			});
			return;
		}

		setIsLoading(true);
		try {
			await verifyEmailChange(trimmed);
			toast({
				title: "Email changed",
				description:
					"Your email was updated. Log in again with the new email.",
				variant: "success",
			});
			onOpenChange(false);
			onSuccess();
		} catch (err: unknown) {
			toast({
				title: "Error",
				description: extractError(err, "Failed to verify code."),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(v) => (!isLoading ? onOpenChange(v) : undefined)}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{step === "enter-email" ? (
							<Mail className="h-5 w-5" />
						) : (
							<ShieldCheck className="h-5 w-5" />
						)}
						{step === "enter-email" ? "Change Email" : "Verify New Email"}
					</DialogTitle>
					<DialogDescription>
						{step === "enter-email"
							? "We'll send a 6-digit code to your new email to confirm ownership."
							: `Enter the code we sent to ${newEmail}.`}
					</DialogDescription>
				</DialogHeader>

				{step === "enter-email" ? (
					<form onSubmit={handleSendCode} className="space-y-4">
						<div className="space-y-2">
							<Label>Current Email</Label>
							<Input value={currentEmail} disabled className="bg-gray-50" />
						</div>
						<div className="space-y-2">
							<Label htmlFor="new-email">New Email</Label>
							<Input
								id="new-email"
								type="email"
								value={newEmail}
								onChange={(e) => setNewEmail(e.target.value)}
								placeholder="you@example.com"
								disabled={isLoading}
								autoComplete="off"
							/>
						</div>
						<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
							After the change, you'll sign in with the new email and will be
							logged out of this session.
						</div>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => onOpenChange(false)}
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading || !newEmail.trim()}>
								{isLoading && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								Send Verification Code
							</Button>
						</DialogFooter>
					</form>
				) : (
					<form onSubmit={handleVerify} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="otp">Verification Code</Label>
							<Input
								id="otp"
								inputMode="numeric"
								autoComplete="one-time-code"
								maxLength={6}
								value={otp}
								onChange={(e) =>
									setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
								}
								placeholder="123456"
								disabled={isLoading}
								className="text-center text-lg font-semibold tracking-[0.5em]"
							/>
							<p className="text-xs text-gray-500">
								Didn't get it? Check spam, or go back and re-send.
							</p>
						</div>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => setStep("enter-email")}
								disabled={isLoading}
							>
								<ArrowLeft className="mr-2 h-4 w-4" />
								Back
							</Button>
							<Button type="submit" disabled={isLoading || otp.length !== 6}>
								{isLoading && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								Confirm Change
							</Button>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
